<script setup lang="ts">
  import { computed, nextTick, ref } from "vue";
  import { Pencil, Plus, Search, Trash2 } from "lucide-vue-next";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { groupByRecency } from "@utils/conversationGroups";
  import { foldText } from "@utils/foldText";

  const TITLE_MAX_LENGTH = 80;

  interface ConversationSummary {
    token: string;
    title: string;
    updated_at: string;
  }

  const props = withDefaults(
    defineProps<{
      conversations?: ConversationSummary[];
      currentConversationToken?: string | null;
      disabled?: boolean;
    }>(),
    { conversations: () => [], currentConversationToken: null, disabled: false },
  );

  const emit = defineEmits<{
    select: [token: string];
    remove: [token: string];
    rename: [token: string, title: string];
    new: [];
  }>();

  const search = ref("");
  const editingToken = ref<string | null>(null);
  const draft = ref("");
  // A ref declared inside v-for collects into an array, and only the row being
  // renamed carries the field, so the array holds it alone.
  const titleInputs = ref<HTMLInputElement[]>([]);

  const matching = computed(() => {
    const needle = foldText(search.value.trim());
    if (!needle) return props.conversations;

    return props.conversations.filter((conversation) => foldText(conversation.title).includes(needle));
  });

  const groups = computed(() => groupByRecency(matching.value));

  const startRename = async (conversation: ConversationSummary) => {
    editingToken.value = conversation.token;
    draft.value = conversation.title;
    await nextTick();
    titleInputs.value[0]?.select();
  };

  const cancelRename = () => {
    editingToken.value = null;
  };

  // Blur also fires as Escape unmounts the field, so a cancelled rename must not
  // come back through the save path.
  const saveRename = (token: string) => {
    if (editingToken.value !== token) return;

    editingToken.value = null;
    emit("rename", token, draft.value);
  };
</script>

<template>
  <div class="flex h-full flex-col gap-3 overflow-hidden rounded-2xl border border-border bg-background p-3">
    <Button
      variant="outline"
      size="sm"
      class="w-full justify-start gap-2 text-xs"
      :disabled="disabled"
      @click="emit('new')">
      <Plus class="h-3.5 w-3.5" />
      New conversation
    </Button>

    <div class="relative">
      <Search class="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
      <Input
        v-model="search"
        placeholder="Search conversations"
        class="h-8 pl-8 text-xs" />
    </div>

    <div class="-mx-1 min-h-0 flex-1 overflow-y-auto px-1">
      <p
        v-if="!groups.length"
        class="px-1 py-4 text-center text-xs text-muted-foreground">
        {{ search ? "No conversation matches" : "No conversation yet" }}
      </p>

      <div
        v-for="group in groups"
        :key="group.label"
        class="mb-3 flex flex-col gap-0.5">
        <span class="px-1.5 pb-1 text-3xs font-medium uppercase tracking-wide text-muted-foreground/70">
          {{ group.label }}
        </span>
        <div
          v-for="conversation in group.items"
          :key="conversation.token"
          class="group flex items-center gap-1 rounded-lg pr-1 transition-colors"
          :class="conversation.token === currentConversationToken ? 'bg-ai-soft' : 'hover:bg-muted'">
          <input
            v-if="conversation.token === editingToken"
            ref="titleInputs"
            v-model="draft"
            :maxlength="TITLE_MAX_LENGTH"
            class="min-w-0 flex-1 rounded-md border border-ai-ring bg-background px-2 py-1 text-xs outline-none"
            @keydown.enter="saveRename(conversation.token)"
            @keydown.esc="cancelRename"
            @blur="saveRename(conversation.token)" />
          <button
            v-else
            type="button"
            class="min-w-0 flex-1 truncate px-2 py-1.5 text-left text-xs"
            :class="conversation.token === currentConversationToken ? 'text-ai-text' : ''"
            :disabled="disabled"
            @click="emit('select', conversation.token)">
            {{ conversation.title }}
          </button>
          <template v-if="conversation.token !== editingToken">
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
              title="Rename this conversation"
              :disabled="disabled"
              @click="startRename(conversation)">
              <Pencil class="h-3.5 w-3.5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              class="h-6 w-6 shrink-0 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100"
              title="Delete this conversation"
              :disabled="disabled"
              @click="emit('remove', conversation.token)">
              <Trash2 class="h-3.5 w-3.5" />
            </Button>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>
